import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { createLazyIcon } from "../../lib/lazy-icons";
import {
  generateDatesFromRule,
  RecurrenceError,
  type RecurrenceFreq,
  type RecurrenceRule,
  MAX_RECURRING_OCCURRENCES,
} from "../../lib/recurrence";

const Repeat = createLazyIcon("Repeat");
const Lock = createLazyIcon("Lock");

export interface RecurrenceConfig {
  rule: RecurrenceRule;
  /** null = no reminders */
  reminderDaysBefore: number | null;
  /**
   * Créneau horaire unique dupliqué sur toutes les occurrences générées
   * (fréquences auto ET mode "custom"). null = "Toute la journée" — pas de
   * timeSlotsByDate généré (comportement d'avant cette feature).
   */
  timeRange: { start: string; end: string } | null;
  /**
   * Quand le créneau global change, écrase aussi les dates dont l'horaire a
   * été édité manuellement (sinon elles sont préservées).
   */
  overwriteManualTimeEdits: boolean;
}

interface RecurrenceSectionProps {
  value: RecurrenceConfig | null;
  onChange: (value: RecurrenceConfig | null) => void;
  hasPaidPlan: boolean;
  isAuthenticated: boolean;
  /** Count of dates currently picked in the calendar (custom mode preview). */
  selectedDatesCount: number;
  /** Nombre de dates dont l'horaire a été édité manuellement — affiche le
   * toggle "écraser" seulement s'il y a quelque chose à écraser. */
  manuallyEditedTimeDatesCount?: number;
  /**
   * Masque l'en-tête (titre + switch on/off + lien upgrade) quand la cadence
   * est pilotée par un contrôle externe (CadenceToggle up-front). Ne rend alors
   * que le corps de configuration.
   */
  hideHeaderToggle?: boolean;
}

const FREQUENCIES: RecurrenceFreq[] = [
  "weekly",
  "biweekly",
  "monthly",
  "daily",
  "custom",
];

const REMINDER_CHOICES = [1, 2, 3, null] as const;

/**
 * Recurring poll configuration section (SOLO feature).
 *
 * Non-custom frequencies generate the occurrence dates from dtstart/until;
 * the parent injects them into the calendar state. "custom" leaves the
 * calendar as the source of dates.
 */
export function RecurrenceSection({
  value,
  onChange,
  hasPaidPlan,
  isAuthenticated,
  selectedDatesCount,
  manuallyEditedTimeDatesCount = 0,
  hideHeaderToggle = false,
}: RecurrenceSectionProps) {
  const { t, i18n } = useTranslation(["creator"]);
  const enabled = value !== null;
  const rule = value?.rule;

  const preview = useMemo(() => {
    if (!rule || rule.freq === "custom") return null;
    if (!rule.dtstart || !rule.until) return null;
    try {
      const dates = generateDatesFromRule(rule);
      return { count: dates.length, error: null as string | null };
    } catch (err) {
      if (err instanceof RecurrenceError) {
        const key =
          err.code === "TOO_MANY_OCCURRENCES"
            ? "recurrence.error_too_many"
            : err.code === "TOO_FEW_OCCURRENCES"
              ? "recurrence.error_too_few"
              : "recurrence.error_invalid";
        return {
          count: 0,
          error: t(`creator:${key}`, { max: MAX_RECURRING_OCCURRENCES }),
        };
      }
      return { count: 0, error: t("creator:recurrence.error_invalid") };
    }
  }, [rule, t]);

  const setRule = (patch: Partial<RecurrenceRule>) => {
    if (!value) return;
    onChange({ ...value, rule: { ...value.rule, ...patch } });
  };

  const toggle = () => {
    if (enabled) {
      onChange(null);
    } else {
      onChange({
        rule: { freq: "weekly", dtstart: "", until: "" },
        reminderDaysBefore: 1,
        timeRange: null,
        overwriteManualTimeEdits: false,
      });
    }
  };

  const isAllDay = !value?.timeRange;
  const setAllDay = (allDay: boolean) => {
    if (!value) return;
    onChange({
      ...value,
      timeRange: allDay ? null : { start: "09:00", end: "10:00" },
    });
  };
  const setTimeRange = (patch: Partial<{ start: string; end: string }>) => {
    if (!value || !value.timeRange) return;
    onChange({ ...value, timeRange: { ...value.timeRange, ...patch } });
  };
  const timeRangeError =
    value?.timeRange &&
    value.timeRange.start &&
    value.timeRange.end &&
    value.timeRange.end <= value.timeRange.start;

  // Locked state: feature needs an authenticated SOLO account.
  const locked = !isAuthenticated || !hasPaidPlan;

  return (
    <div
      className="border border-border rounded-lg bg-card p-4 space-y-3"
      data-testid="recurrence-section"
    >
      {!hideHeaderToggle && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-foreground">
            <Repeat className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t("creator:recurrence.title")}
            </span>
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/40">
              {t("creator:recurrence.solo_badge")}
            </span>
          </div>

          {locked ? (
            <Link
              to="/pricing"
              className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80"
              data-testid="recurrence-upgrade-link"
            >
              <Lock className="w-3.5 h-3.5" />
              {t("creator:recurrence.upgrade_cta")}
            </Link>
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label={t("creator:recurrence.title")}
              onClick={toggle}
              data-testid="recurrence-toggle"
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                enabled ? "bg-primary" : "bg-muted"
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-background transition-transform ${
                  enabled ? "translate-x-[18px]" : "translate-x-[3px]"
                }`}
              />
            </button>
          )}
        </div>
      )}

      {!hideHeaderToggle && locked && (
        <p className="text-xs text-muted-foreground">
          {t("creator:recurrence.locked_description")}
        </p>
      )}

      {enabled && value && (
        <div className="space-y-3" data-testid="recurrence-config">
          {/* Frequency */}
          <div className="flex flex-wrap gap-2">
            {FREQUENCIES.map((freq) => (
              <button
                key={freq}
                type="button"
                onClick={() => setRule({ freq })}
                data-testid={`recurrence-freq-${freq}`}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  value.rule.freq === freq
                    ? "border-primary bg-primary/15 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/60"
                }`}
              >
                {t(`creator:recurrence.freq_${freq}`)}
              </button>
            ))}
          </div>

          {/* Créneau horaire — dupliqué sur toutes les occurrences générées,
              fréquences auto ET mode "custom". */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={isAllDay}
                onChange={(e) => setAllDay(e.target.checked)}
                data-testid="recurrence-all-day"
                className="accent-primary"
              />
              {t("creator:recurrence.all_day_label")}
            </label>

            {!isAllDay && value.timeRange && (
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-muted-foreground flex flex-col gap-1">
                  {t("creator:recurrence.start_time_label")}
                  <input
                    type="time"
                    value={value.timeRange.start}
                    onChange={(e) => setTimeRange({ start: e.target.value })}
                    data-testid="recurrence-start-time"
                    className="bg-background border border-border rounded-lg px-2 py-1.5 text-sm text-foreground"
                  />
                </label>
                <label className="text-xs text-muted-foreground flex flex-col gap-1">
                  {t("creator:recurrence.end_time_label")}
                  <input
                    type="time"
                    value={value.timeRange.end}
                    onChange={(e) => setTimeRange({ end: e.target.value })}
                    data-testid="recurrence-end-time"
                    className="bg-background border border-border rounded-lg px-2 py-1.5 text-sm text-foreground"
                  />
                </label>
                {timeRangeError && (
                  <p
                    className="text-xs text-destructive"
                    data-testid="recurrence-time-range-error"
                  >
                    {t("creator:recurrence.time_range_invalid_error")}
                  </p>
                )}
              </div>
            )}

            {!isAllDay && manuallyEditedTimeDatesCount > 0 && (
              <label className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted px-3 py-2">
                <span className="text-xs text-muted-foreground">
                  {t("creator:recurrence.overwrite_manual_hint", {
                    count: manuallyEditedTimeDatesCount,
                  })}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={value.overwriteManualTimeEdits}
                  onClick={() =>
                    onChange({
                      ...value,
                      overwriteManualTimeEdits: !value.overwriteManualTimeEdits,
                    })
                  }
                  data-testid="recurrence-overwrite-manual-toggle"
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                    value.overwriteManualTimeEdits ? "bg-primary" : "bg-muted"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-background transition-transform ${
                      value.overwriteManualTimeEdits
                        ? "translate-x-[18px]"
                        : "translate-x-[3px]"
                    }`}
                  />
                </button>
              </label>
            )}
          </div>

          {value.rule.freq === "custom" ? (
            <p className="text-xs text-muted-foreground">
              {t("creator:recurrence.custom_hint", {
                count: selectedDatesCount,
              })}
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs text-muted-foreground flex flex-col gap-1">
                {t("creator:recurrence.from")}
                <input
                  type="date"
                  value={value.rule.dtstart ?? ""}
                  onChange={(e) => setRule({ dtstart: e.target.value })}
                  data-testid="recurrence-dtstart"
                  className="bg-background border border-border rounded-lg px-2 py-1.5 text-sm text-foreground"
                />
              </label>
              <label className="text-xs text-muted-foreground flex flex-col gap-1">
                {t("creator:recurrence.until")}
                <input
                  type="date"
                  value={value.rule.until ?? ""}
                  min={value.rule.dtstart || undefined}
                  onChange={(e) => setRule({ until: e.target.value })}
                  data-testid="recurrence-until"
                  className="bg-background border border-border rounded-lg px-2 py-1.5 text-sm text-foreground"
                />
              </label>
              {preview &&
                (preview.error ? (
                  <p
                    className="text-xs text-destructive"
                    data-testid="recurrence-error"
                  >
                    {preview.error}
                  </p>
                ) : (
                  <p
                    className="text-xs text-success"
                    data-testid="recurrence-preview"
                  >
                    {t("creator:recurrence.preview_count", {
                      count: preview.count,
                    })}
                  </p>
                ))}
            </div>
          )}

          {/* Reminders */}
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">
              {t("creator:recurrence.reminder_label")}
            </p>
            <div className="flex flex-wrap gap-2">
              {REMINDER_CHOICES.map((days) => (
                <button
                  key={days ?? "never"}
                  type="button"
                  onClick={() =>
                    onChange({ ...value, reminderDaysBefore: days })
                  }
                  data-testid={`recurrence-reminder-${days ?? "never"}`}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                    value.reminderDaysBefore === days
                      ? "border-primary bg-primary/15 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/60"
                  }`}
                >
                  {days === null
                    ? t("creator:recurrence.reminder_never")
                    : t("creator:recurrence.reminder_days", { count: days })}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t("creator:recurrence.reminder_hint", {
                timezone: Intl.DateTimeFormat(i18n.language).resolvedOptions()
                  .timeZone,
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecurrenceSection;
